/* eslint-disable max-lines-per-function */
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import type { ExtensionApi } from '../../../core/extension';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicBeeSwarm',
  name_en: 'Dynamic BeeSwarm',
  name_zh: '动态蜂群图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    y: amount => amount.length >= 2,
    y2: amount => amount > 1,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.ARRAY
    },
    y2: { itemType: [DEFINE.DATE], category: DEFINE.SINGLE }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string[];
    y2: string;
  };
  animationDuration: string;
}

/** 暂不支持legend的点击功能 */
export const dynamicBeeSwarm = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  withTimeline: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util: utils, hook, layout } = api;
    const presetOption = hook.buildStandardChartPresetOption(api);
    const {
      dataIndex = 0,
      encoding: { x: name, y: valuesArray, y2: groupBy },
      animationDuration: updateFrequency = 1200
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * ------------------------- data
    const [y, size = '__$$size__'] = valuesArray;
    // ! 将 size 作为可选的
    if (size === '__$$size__') {
      data.values.forEach(item => {
        item[size] = 1;
      });
    }
    const nameSortList = utils.dataTransform.transform(data.values.slice(), [
      { type: 'deDuplication', field: name }
    ]);
    const { nameList } = utils.dataEncoding.encoding(nameSortList, {
      nameList: { field: name }
    });

    const datesSortList = utils.dataTransform.transform(data.values.slice(), [
      { type: 'deDuplication', field: groupBy },
      { type: 'sort', field: groupBy, order: 'ascending' }
    ]);
    const { dates } = utils.dataEncoding.encoding(datesSortList, {
      dates: { field: groupBy }
    });
    const nameColorMap = {};
    const colorList = presetOption?.color || [
      '#FF9500',
      '#14CCBD',
      '#1A9FFF',
      '#3366FF',
      '#4433FF',
      '#FF33AA',
      '#CC41D9',
      '#FFAD29',
      '#38D9C6',
      '#42B7FF',
      '#5C8AFF',
      '#6F5CFF',
      '#FF5CB6',
      '#DF6AE6',
      '#FFC252',
      '#60E6D2',
      '#6BCBFF',
      '#85ABFF',
      '#9785FF',
      '#FF85C4',
      '#F196F2',
      '#FFD57A',
      '#8DF2E0',
      '#94DDFF',
      '#ADCAFF',
      '#BCADFF',
      '#FFADD5',
      '#FFC7FE'
    ];
    nameList.forEach((item, index) => {
      nameColorMap[item] = colorList[index % colorList.length];
    });

    // 格式化
    const { allValues, allSizes } = utils.dataEncoding.encoding(data.values.slice(), {
      allValues: { field: y, transforms: [{ type: 'toNumeric', field: y }] },
      allSizes: { field: size, transforms: [{ type: 'toNumeric', field: size }] }
    });
    const { beeswarmHelper } = ThsDataVStandardChart.helper;
    const sizeRangeMap = {
      'sm-1*1': [8, 28],
      'sm-2*1': [8, 46],
      'sm-1*2': [8, 46],
      'lg-1*1': [16, 92],
      'lg-3*2': [20, 120]
    };
    const range = sizeRangeMap[layout];
    const symbolSizeScale = beeswarmHelper.getSymbolSizeScale(allSizes, range);
    const formatData = data.values.map(d => {
      return {
        id: d[name],
        name: d[name],
        value: +d[y],
        symbolSize: symbolSizeScale(+d[size]),
        date: d[groupBy],
        itemStyle: {
          color: nameColorMap[d[name]]
        }
      };
    });

    // 解析数据-数据分组
    const datas = dates.map(date => {
      return utils.dataTransform.transform(formatData.slice(), [
        { type: 'filter', cb: item => item.date === date }
      ]);
    });
    const [min, max] = beeswarmHelper.getAxisRange(allValues);
    const yRange = max - min;
    const fixMin = Math.floor(min - yRange * 0.4);
    const fixMax = Math.ceil(max + yRange * 0.4);
    const formattedMaxValue = hook.formatDataNumberValue(fixMax);
    const maxValueTextWidth = utils.canvas.measureTextWidth(
      Number(formattedMaxValue.value).toFixed(0) + formattedMaxValue.unit,
      presetOption.textStyle.fontSize,
      presetOption.textStyle.fontFamily
    );

    // 封装播放函数
    let chartIns: unknown;
    let option;
    let legendIns: InstanceType<ExtensionApi['ui']['Legend']['Legend']>;

    return renderApi => {
      function playByIndex(index) {
        const NAME_MARGIN = 6;
        option = {
          animationDurationUpdate: updateFrequency,
          animationEasingUpdate: 'linear',
          grid: [
            {
              top: 0,
              bottom: 4,
              left: 0,
              right: 0
            }
          ],
          tooltip: {
            formatter: params_ => {
              const { color, seriesType, name, value, seriesName } = params_;
              const result = hook.formatDataNumberValue(value, {
                type: 'table',
                unit: dataMetaInfo[seriesName]?.unit
              });
              const items = [
                {
                  type: utils.const.SERIES_2_LEGEND_TYPE_MAP[seriesType],
                  color,
                  name: '',
                  value: ''
                },
                {
                  name: y,
                  value: `${result.value + result.unit}`
                }
              ];

              return hook.buildTooltip({
                config: hook.buildStandardChartPresetOption(api).tooltip,
                title: name || '',
                items
              });
            }
          },
          xAxis: [
            {
              show: true,
              showMaxLabel: true,
              showMinLabel: true,
              axisLine: {
                show: false
              },
              axisTick: {
                show: false
              }
            }
          ],
          axisPointer: {
            show: false
          },
          yAxis: [
            {
              show: true,
              name: y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : ''),
              dvNameLocation: 'left',
              nameGap: -presetOption.textStyle.fontSize,
              nameTextStyle: {
                padding: [0, 0, 0, maxValueTextWidth + NAME_MARGIN]
              },
              axisLine: {
                show: false
              },
              axisTick: {
                show: false
              },
              min: fixMin,
              max: fixMax,
              axisLabel: {
                inside: true,
                dvAlignEdge: true,
                margin: 0,
                verticalAlign: 'bottom',
                formatter(value) {
                  const result = hook.formatDataNumberValue(value);
                  return result.value + result.unit;
                }
              }
            }
          ],
          series: [
            {
              type: 'graph',
              layout: 'beeswarm',
              beeswarm: {
                axisIndex: 0
              },
              nodes: datas[index],
              data: datas[index],
              width: '100%',
              height: '100%'
            }
          ]
        };

        option = renderApi.mergeChartOptionProxy(option);
        renderApi.optionCallbackProxy(option);

        const renderParams = { option };
        if (!chartIns) {
          chartIns = renderApi.renderChartProxy(renderParams, { theme: 'pc-wencai-light' });
        } else {
          api.hook.beforeRenderStandardChart(api, renderParams);
          chartIns.play(renderParams);

          renderApi.cacheRenderOptionProxy(renderParams.option);
        }
        if (!legendIns) {
          legendIns = renderApi.renderLegendProxy(renderParams.option.legend.data) as InstanceType<
            ExtensionApi['ui']['Legend']['Legend']
          >;
        } else {
          legendIns?.update(renderParams.option.legend.data);
        }
      }

      const timelineIns = renderApi.renderTimelineProxy(dates as string[]);

      // 渲染第一周期
      playByIndex(dates.length - 1);

      timelineIns?.on?.('change', e => {
        playByIndex(e.index);
      });
    };
  }
});
